<template>
  <div class="waajo-contact-selector">
    <div v-if="selected" class="waajo-contact-selected">
      <span class="waajo-contact-avatar">{{ selectedInitial }}</span>
      <div class="waajo-contact-selected-copy">
        <strong>{{ selectedName }}</strong>
        <small>{{ formattedSelectedPhone }}</small>
        <small v-if="selected.email">{{ selected.email }}</small>
      </div>
      <button type="button" class="waajo-contact-clear" :disabled="disabled" title="Ganti kontak" @click="clear">
        <i class="fa fa-times"></i>
      </button>
    </div>

    <div v-else class="waajo-contact-search-wrap">
      <div class="waajo-contact-search" :class="{ 'is-open': open }">
        <i class="fa fa-search"></i>
        <input
          v-model.trim="keyword"
          type="text"
          autocomplete="off"
          placeholder="Cari nama atau nomor WhatsApp..."
          :disabled="disabled"
          @input="onInput"
          @focus="openResults"
          @keyup.esc="open = false"
        >
        <i v-if="loading" class="fa fa-spinner fa-spin"></i>
        <button v-else-if="keyword" type="button" class="waajo-contact-search-clear" @click="clearKeyword">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div v-if="open" class="waajo-contact-dropdown">
        <div v-if="loading" class="waajo-contact-state">
          <i class="fa fa-spinner fa-spin"></i>
          <span>Mencari kontak...</span>
        </div>
        <div v-else-if="errorMessage" class="waajo-contact-state is-error">
          <i class="fa fa-exclamation-circle"></i>
          <span>{{ errorMessage }}</span>
          <button type="button" @click="searchContacts">Coba lagi</button>
        </div>
        <div v-else-if="!results.length" class="waajo-contact-state">
          <i class="fa fa-user-o"></i>
          <span>Kontak tidak ditemukan.</span>
        </div>
        <button
          v-for="contact in results"
          :key="contact.id"
          type="button"
          class="waajo-contact-option"
          @click="selectContact(contact)"
        >
          <span class="waajo-contact-avatar is-small">{{ initialFor(contact) }}</span>
          <span class="waajo-contact-option-copy">
            <strong>{{ fullName(contact) }}</strong>
            <small>{{ formatTicketPhone(contact.phone_number) }}</small>
          </span>
          <i class="fa fa-angle-right"></i>
        </button>
      </div>
    </div>

    <button type="button" class="waajo-contact-add-btn" :disabled="disabled" @click="$emit('add-contact')">
      <i class="fa fa-plus"></i> Tambah Kontak Baru
    </button>
  </div>
</template>

<script>
import { formatTicketPhone, ticketContactNumber, extractApiErrorMessage } from '@/utils/ticket'

export default {
  name: 'TicketContactSelector',
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      keyword: '',
      results: [],
      selected: null,
      open: false,
      loading: false,
      errorMessage: '',
      requestId: 0,
      searchTimer: null
    }
  },
  computed: {
    selectedName() {
      return this.selected ? this.fullName(this.selected) : ''
    },
    selectedInitial() {
      return this.selected ? this.initialFor(this.selected) : '?'
    },
    formattedSelectedPhone() {
      return this.selected ? formatTicketPhone(this.selected.phone_number) : '-'
    }
  },
  beforeDestroy() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer)
    }
    document.removeEventListener('click', this.handleOutsideClick)
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  methods: {
    handleOutsideClick(event) {
      if (this.$el && !this.$el.contains(event.target)) {
        this.open = false
      }
    },
    formatTicketPhone,
    normalizeContact(contact) {
      if (!contact) return null
      return Object.assign({}, contact, {
        id: String(contact.id || contact.Id || '').trim(),
        name: String(contact.name || '').trim(),
        first_name: String(contact.first_name || '').trim(),
        last_name: String(contact.last_name || '').trim(),
        phone_number: ticketContactNumber(contact),
        email: String(contact.email || '').trim()
      })
    },
    fullName(contact) {
      if (!contact) return 'Tanpa Nama'
      const explicitName = String(contact.name || '').trim()
      if (explicitName) return explicitName
      const full = ((contact.first_name || '') + ' ' + (contact.last_name || '')).trim()
      return full || 'Tanpa Nama'
    },
    initialFor(contact) {
      const name = this.fullName(contact)
      return name ? name.charAt(0).toUpperCase() : '?'
    },
    onInput() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      this.searchTimer = setTimeout(() => {
        this.searchTimer = null
        this.searchContacts()
      }, 320)
    },
    openResults() {
      if (this.disabled) return
      this.open = true
      if (!this.results.length && !this.loading) {
        this.searchContacts()
      }
    },
    clearKeyword() {
      this.keyword = ''
      this.results = []
      this.errorMessage = ''
      this.searchContacts()
    },
    async searchContacts() {
      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.errorMessage = ''
      this.open = true
      try {
        const response = await this.$store.dispatch('contact/getContactList', {
          keywords: this.keyword,
          phone_number: '',
          email: '',
          name: '',
          group_id: [],
          tag_id: [],
          page: 0,
          perPage: 20,
          rendered: 0,
          totalRecords: 0
        })
        if (requestId !== this.requestId) return
        const payload = response || {}
        const rows = Array.isArray(payload.data) ? payload.data : []
        this.results = rows
          .filter(row => {
            if (Number(row.is_group) === 1) return false
            return String(row.phone_number || row.mobile_phone || row.contact_number || '').trim() !== ''
          })
          .map(row => this.normalizeContact(row))
      } catch (error) {
        if (requestId !== this.requestId) return
        this.results = []
        this.errorMessage = extractApiErrorMessage(error, 'Daftar kontak gagal dimuat.')
      } finally {
        if (requestId === this.requestId) {
          this.loading = false
        }
      }
    },
    selectContact(contact) {
      if (!contact) return
      const normalized = this.normalizeContact(contact)
      if (!normalized || !normalized.phone_number) return
      this.selected = normalized
      this.open = false
      this.keyword = ''
      this.results = []
      this.$emit('selected', this.selected)
    },
    clear() {
      this.selected = null
      this.$emit('selected', null)
      this.$nextTick(() => {
        this.openResults()
      })
    },
    clearSelection() {
      this.selected = null
    }
  }
}
</script>

<style scoped>
.waajo-contact-selector {
  position: relative;
  display: block;
  width: 100%;
}
.waajo-contact-search-wrap {
  position: relative;
  display: block;
  width: 100%;
}
.waajo-contact-search {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-height: 42px;
  box-sizing: border-box;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  padding: 0 12px;
}
.waajo-contact-search.is-open,
.waajo-contact-search:focus-within {
  border-color: var(--waajo-brand-border-strong);
  box-shadow: 0 0 0 3px var(--waajo-focus-ring);
}
.waajo-contact-search > i {
  color: var(--waajo-text-faint);
  font-size: 14px;
}
.waajo-contact-search input {
  flex: 1;
  border: 0;
  outline: none;
  min-height: 40px;
  font-size: 14px;
  color: var(--waajo-text);
  background: transparent;
}
.waajo-contact-search input::placeholder {
  color: var(--waajo-text-faint);
}
.waajo-contact-search-clear {
  border: 0;
  background: #eef2f7;
  color: var(--waajo-text-muted);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 10px;
}
.waajo-contact-dropdown {
  position: absolute;
  z-index: 40;
  left: 0;
  right: 0;
  top: calc(100% + 6px);
  max-height: 260px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid var(--waajo-border);
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.16);
}
.waajo-contact-option {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: 0;
  border-radius: 8px;
  background: transparent;
  padding: 9px 8px;
  text-align: left;
}
.waajo-contact-option:hover,
.waajo-contact-option:focus {
  background: var(--waajo-brand-soft-2);
}
.waajo-contact-option-copy {
  flex: 1;
  min-width: 0;
}
.waajo-contact-option-copy strong {
  display: block;
  font-size: 14px;
  color: var(--waajo-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.waajo-contact-option-copy small {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-contact-option > i {
  color: var(--waajo-text-faint);
  font-size: 12px;
}
.waajo-contact-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 50%;
  background: var(--waajo-brand-soft);
  color: var(--waajo-brand-ink);
  font-size: 15px;
  font-weight: 700;
}
.waajo-contact-avatar.is-small {
  width: 34px;
  height: 34px;
  flex-basis: 34px;
  font-size: 13px;
}
.waajo-contact-selected {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 60px;
  padding: 9px 11px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 9px;
  background: var(--waajo-brand-soft-2);
}
.waajo-contact-selected-copy {
  flex: 1;
  min-width: 0;
}
.waajo-contact-selected-copy strong {
  display: block;
  font-size: 14px;
  color: var(--waajo-text);
}
.waajo-contact-selected-copy small {
  display: block;
  margin-top: 2px;
  font-size: 12px;
  color: var(--waajo-text-muted);
}
.waajo-contact-clear {
  border: 0;
  background: transparent;
  color: var(--waajo-text-muted);
  font-size: 14px;
  padding: 4px;
}
.waajo-contact-add-btn {
  border: 0;
  background: transparent;
  color: var(--waajo-brand-ink);
  font-size: 13px;
  font-weight: 700;
  padding: 7px 0 0;
}
.waajo-contact-add-btn:disabled {
  opacity: 0.6;
}
.waajo-contact-state {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 12px 9px;
  font-size: 13px;
  color: var(--waajo-text-muted);
}
.waajo-contact-state.is-error {
  color: #b42318;
}
.waajo-contact-state button {
  border: 0;
  background: transparent;
  color: #b42318;
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
  padding: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketContactSelector.vue